import { InfoOutlined } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import EmptyStateBox from 'src/components/common/ui/EmptyStateBox';
import { RequestHandlerProps } from 'src/components/RequestHandler/RequestHandler.types';
import ListSkeleton from 'src/components/Skeletons/ListSkeleton';
import {
  CenteredContentContainer,
  FlexColumnContainer,
  RoundedIconContainer,
} from 'src/components/styles';
import isEmpty from 'src/utils/isEmpty';

const RequestHandler = <T, F>({
  isLoading,
  isError,
  data,
  filters,
  onClearFilters,
  skeleton,
  refetch,
  children,
  variant = 'default',
  errorProps = {
    title: 'Error al cargar los datos',
    subtitle: 'Ocurrió un problema. Por favor, intenta de nuevo.',
  },
  emptyStateProps = {
    title: 'Sin datos disponibles',
    subtitle: 'No hay información para mostrar en este momento.',
  },
}: RequestHandlerProps<T, F>) => {
  const navigate = useNavigate();
  const location = useLocation();

  if (isLoading) {
    return skeleton || <ListSkeleton />;
  }

  const handleBack = () => {
    const previousPath = location.state?.from;

    if (!previousPath) {
      return navigate('/');
    }

    navigate(previousPath);
  };

  const handleBackErrorState = () => {
    if (emptyStateProps.onButtonClick) {
      emptyStateProps.onButtonClick();
    } else {
      handleBack();
    }
  };

  if (isError) {
    return (
      <CenteredContentContainer py={2}>
        {variant === 'message' ? (
          <Typography
            py={2}
            variant="body1"
            fontWeight="fontWeightMedium"
            color="InactiveCaptionText"
          >
            {errorProps.title}
          </Typography>
        ) : (
          <EmptyStateBox
            title={errorProps.title}
            subtitle={errorProps.subtitle}
            icon={
              <RoundedIconContainer
                bgcolor={({ palette }) => palette.primary.main}
              >
                <InfoOutlined fontSize="large" color="inherit" />
              </RoundedIconContainer>
            }
            primaryButton={
              <Button
                variant="contained"
                sx={{ minWidth: '260px' }}
                color="primary"
                onClick={refetch}
              >
                Reintentar
              </Button>
            }
            secondaryButton={
              <Button
                variant="outlined"
                color="primary"
                onClick={handleBackErrorState}
                sx={{ minWidth: '260px' }}
              >
                {errorProps.buttonText ?? 'Volver'}
              </Button>
            }
          />
        )}
      </CenteredContentContainer>
    );
  }

  const hasFilters = !isEmpty(filters);

  if (!data || isEmpty(data)) {
    return (
      <CenteredContentContainer py={2}>
        {hasFilters ? (
          <FlexColumnContainer py={3} gap={3}>
            <Typography
              variant="body1"
              color="InactiveCaptionText"
              fontWeight="fontWeightMedium"
            >
              No se encontraron resultados
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={onClearFilters}
            >
              Limpiar filtros
            </Button>
          </FlexColumnContainer>
        ) : variant === 'message' ? (
          <Typography
            py={2}
            variant="body1"
            color="InactiveCaptionText"
            fontWeight="fontWeightMedium"
          >
            {emptyStateProps.title}
          </Typography>
        ) : (
          <EmptyStateBox
            title={emptyStateProps.title}
            subtitle={emptyStateProps.subtitle}
            icon={
              <RoundedIconContainer
                bgcolor={({ palette }) => palette.primary.main}
              >
                <InfoOutlined fontSize="large" color="inherit" />
              </RoundedIconContainer>
            }
            primaryButton={
              <Button variant="outlined" color="primary" onClick={handleBack}>
                {emptyStateProps.buttonText ?? 'Volver'}
              </Button>
            }
          />
        )}
      </CenteredContentContainer>
    );
  }

  return children(data);
};

export default RequestHandler;
